<!--suppress ALL -->
<template>
	<div class="autocomplete control" :class="{'is-expanded': expanded}">
		<g-input v-model="newValue" ref="input"
						 :size="size" :is-loading="isLoading"
						 :type="type" :position="position" :shape="shape"
						 :isExpanded="isExpanded" :maxlength="maxlength"
						 autocomplete="off" v-bind="$attrs"
						 @focus="focused" @blur="onBlur"
             :placeholder="placeholder"
						 @keyup.native.esc.prevent="isActive = false"
						 @keydown.native.tab="tabPressed"
						 @keydown.native.enter.prevent="enterPressed"
						 @keydown.native.up.prevent="keyArrows('up')"
						 @keydown.native.down.prevent="keyArrows('down')"
		>
			<template v-if="$slots.iconLeft" slot="iconLeft">
				<slot name="iconLeft"></slot>
			</template>
			<template v-if="$slots.iconRight" slot="iconRight">
				<slot name="iconRight"></slot>
			</template>
		</g-input>

		<transition name="fade">
			<div
					class="dropdown-menu"
					:class="{ 'is-opened-top': !isListInViewportVertically }"
					v-show="isActive && (data.length > 0 || hasEmptySlot || hasHeaderSlot|| hasFooterSlot)"
					ref="dropdown">
				<div class="dropdown-content">
					<div
							v-if="hasHeaderSlot"
							class="dropdown-item">
						<slot name="header"/>
					</div>
					<a
							v-show="isActive"
							v-for="(option, index) in data"
							:key="index"
							class="dropdown-item"
							:class="{ 'is-hovered': option === hovered }"
							@click="setSelected(option)">

						<slot
								v-if="hasDefaultSlot"
								:option="option"
								:index="index"
						/>
						<span v-else v-html="getValue(option, true)"/>
					</a>
					<div
							v-if="data.length === 0 && hasEmptySlot"
							class="dropdown-item is-disabled">
						<slot name="empty"/>
					</div>
					<div
							v-if="hasFooterSlot"
							class="dropdown-item">
						<slot name="footer"/>
					</div>
				</div>
			</div>
		</transition>
	</div>
</template>

<script lang="ts">
  import {Component, Mixin, Prop, Watch} from 'annotation';
  import {getValueByPath, escapeRegExpChars} from '../../../../util/Underscore';
  import FormPropMixins from '../../../../util/mixin/FormProperties';
  import Input from '../input/GInput.vue';

  @Component({
    components: {'g-input': Input},
    inheritAttrs: false,
    data() {
      return {
        newValue: this.value
      }
    }
  })
  export default class GAutocomplete extends Mixin(FormPropMixins) {

    @Prop()
    public value: any;

    @Prop({type: Array, default: () => []})
    public data: Array<any>;

    @Prop({type: String, default: 'value'})
    public field: string;

    @Prop({type: String, default: 'Cari disini ...'})
    public placeholder: string;

    @Prop({type: Boolean})
    public keepFirst: boolean;

    @Prop({type: Boolean})
    public clearOnSelect: boolean;

    @Prop({type: Boolean})
    public openOnFocus: boolean;

    public selected: any = null;

    public hovered: any = null;

    public isValid: any = true;

    public isActive: boolean = false;

    public newValue: any;

    public isListInViewportVertically: boolean = true;

    public hasFocus: boolean = false;

    public _isAutocomplete: boolean = true;

    public _elementRef: string = 'input';

    /**
     * White-listed items to not close when clicked.
     * Add input, dropdown and all children.
     */
    public get whiteList() {
      const whiteList = []
      whiteList.push(this.$refs.input.$el.querySelector('input'))
      whiteList.push(this.$refs.dropdown)
      // Add all chidren from dropdown
      if (this.$refs.dropdown !== undefined) {
        const children = this.$refs.dropdown.querySelectorAll('*')
        for (const child of children) {
          whiteList.push(child)
        }
      }

      return whiteList
    }

    /**
     * Check if exists default slot
     */
    public get hasDefaultSlot() {
      return !!this.$scopedSlots.default
    }

    /**
     * Check if exists "empty" slot
     */
    public get hasEmptySlot() {
      return !!this.$slots.empty
    }

    /**
     * Check if exists "header" slot
     */
    public get hasHeaderSlot() {
      return !!this.$slots.header
    }

    /**
     * Check if exists "footer" slot
     */
    public get hasFooterSlot() {
      return !!this.$slots.footer
    }

    /**
     * When dropdown is toggled, check the visibility to know when
     * to open upwards.
     */
    @Watch('isActive')
    public onIsActiveChange(active) {
      if (active) {
        this.calcDropdownInViewportVertical()
      } else {
        this.$nextTick(() => this.setHovered(null))
        // Timeout to wait for the animation to finish before recalculating
        setTimeout(() => {
          this.calcDropdownInViewportVertical()
        }, 100)
      }
    }

    /**
     * When updating input's value
     *   1. Emit changes
     *   2. If value isn't the same as selected, set null
     *   3. Close dropdown if value is clear or else open it
     */
    public created() {
      if (typeof window !== 'undefined') {
        document.addEventListener('click', this.clickedOutside)
        window.addEventListener('resize', this.calcDropdownInViewportVertical)
      }

      // Manual watch for newValue to ensure it works in production
      this.$watch('newValue', (value) => {
        this.$emit('input', value);
        // Check if selected is invalid
        const currentValue = this.getValue(this.selected);
        if (currentValue && currentValue !== value) {
          this.setSelected(null, false);
        }
        // Close dropdown if input is clear or else open it
        if (this.hasFocus && (!this.openOnFocus || value)) {
          this.isActive = !!value;
        }
      });
    }

    public beforeDestroy() {
      if (typeof window !== 'undefined') {
        document.removeEventListener('click', this.clickedOutside)
        window.removeEventListener('resize', this.calcDropdownInViewportVertical)
      }
    }

    /**
     * Set which option is currently hovered.
     */
    public setHovered(option) {
      if (option === undefined) return

      this.hovered = option
    }

    /**
     * Set which option is currently selected, update v-model,
     * update input value and close dropdown.
     */
    public setSelected(option, closeDropdown = true) {
      if (option === undefined) return

      this.selected = option
      this.$emit('select', this.selected)
      if (this.selected !== null) {
        this.newValue = this.clearOnSelect ? '' : this.getValue(this.selected)
      }
      closeDropdown && this.$nextTick(() => {
        this.isActive = false
      })
    }

    /**
     * Select first option
     */
    public selectFirstOption(options) {
      this.$nextTick(() => {
        if (options.length) {
          // If has visible data or open on focus, keep updating the hovered
          if (this.openOnFocus || (this.newValue !== '' && this.hovered !== options[0])) {
            this.setHovered(options[0])
          }
        } else {
          this.setHovered(null)
        }
      })
    }

    /**
     * Enter key listener.
     * Select the hovered option.
     */
    public enterPressed() {
      if (this.hovered === null) return
      this.setSelected(this.hovered)
    }

    /**
     * Tab key listener.
     * Select hovered option if it exists, close dropdown, then allow
     * native handling to move to next tabbable element.
     */
    public tabPressed() {
      if (this.hovered === null) {
        this.isActive = false
        return
      }
      this.setSelected(this.hovered)
    }

    /**
     * Close dropdown if clicked outside.
     */
    public clickedOutside(event) {
      if (this.whiteList.indexOf(event.target) < 0) this.isActive = false
    }

    /**
     * Return display text for the input.
     * If object, get value from path, or else just the value.
     * If hightlight, find the text with regex and make bold.
     */
    public getValue(option, isHighlight = false) {
      if (!option) return

      const value = typeof option === 'object'
        ? getValueByPath(option, this.field)
        : option

      const escapedValue = typeof this.newValue === 'string'
        ? escapeRegExpChars(this.newValue)
        : this.newValue
      const regex = new RegExp(`(${escapedValue})`, 'gi')

      return isHighlight
        ? value.replace(regex, '<b>$1</b>')
        : value
    }

    /**
     * Calculate if the dropdown is vertically visible when activated,
     * otherwise it is openened upwards.
     */
    public calcDropdownInViewportVertical() {
      this.$nextTick(() => {
        /**
         * this.$refs.dropdown may be undefined
         * when Autocomplete is conditional rendered
         */
        if (this.$refs.dropdown === undefined) return

        const rect = this.$refs.dropdown.getBoundingClientRect()

        this.isListInViewportVertically = (
          rect.top >= 0 &&
          rect.bottom <= (window.innerHeight ||
            document.documentElement.clientHeight)
        )
      })
    }

    /**
     * Arrows keys listener.
     * If dropdown is active, set hovered option, or else just open.
     */
    public keyArrows(direction) {
      const sum = direction === 'down' ? 1 : -1
      if (this.isActive) {
        let index = this.data.indexOf(this.hovered) + sum
        index = index > this.data.length - 1 ? this.data.length : index
        index = index < 0 ? 0 : index

        this.setHovered(this.data[index])

        const list = this.$refs.dropdown.querySelector('.dropdown-content')
        const element = list.querySelectorAll('.dropdown-item:not(.is-disabled)')[index]

        if (!element) return

        const visMin = list.scrollTop
        const visMax = list.scrollTop + list.clientHeight - element.clientHeight

        if (element.offsetTop < visMin) {
          list.scrollTop = element.offsetTop
        } else if (element.offsetTop >= visMax) {
          list.scrollTop = (
            element.offsetTop -
            list.clientHeight +
            element.clientHeight
          )
        }
      } else {
        this.isActive = true
      }
    }

    /**
     * Focus listener.
     * If value is the same as selected, select all text.
     */
    public focused(event) {
      if (this.getValue(this.selected) === this.newValue) {
        this.$el.querySelector('input').select()
      }
      if (this.openOnFocus) {
        this.isActive = true
        if (this.keepFirst) {
          this.selectFirstOption(this.data)
        }
      }
      this.hasFocus = true
      this.$emit('focus', event)
    }

    /**
     * Blur listener.
     */
    public onBlur(event) {
      this.hasFocus = false
      this.$emit('blur', event)
    }
  }
</script>

<style lang="scss" scoped>
	@import "../../../../style/variables_function";

	.autocomplete {
		position: relative;

		.dropdown-menu {
			display: block;
			min-width: 100%;
			max-width: 100%;

			&.is-opened-top {
				top: auto;
				bottom: 100%;
			}
		}

		.dropdown-content {
			overflow: auto;
			max-height: 200px;
		}

		.dropdown-item {
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;

			&.is-hovered {
				background: $white-ter;
				color: $grey-dark;
			}

			&.is-disabled {
				opacity: 0.5;
				cursor: not-allowed;
			}
		}

		&.is-small {
			@include control-small;
		}

		&.is-medium {
			@include control-medium;
		}

		&.is-large {
			@include control-large;
		}
	}
</style>
